import { AskDataMode } from './AskDataMode';
import { IRemoteAgentSiteSettings, ISiteAdminBridgeSettings } from './ISiteAdminBridgeSettings';
import { ISiteCommentingSettings } from './ISiteCommentingSettings';

// a generic interface that allows us to pass bridge settings, extension settings, etc to the coordinator ctrl
type SiteAdminSettingsCombined = ISiteAdminSettings | IDashExtSiteSettings | ISiteAdminBridgeSettings;

export interface ISiteSettingsForSiteAdmin {
  siteId: string;
  siteAdminSettings: SiteAdminSettingsCombined;
  subscriptionSettings?: ISiteSubscriptionSettings;
}

export interface ISiteAdminSettings {
  addSupportUserEnabled: boolean;
  attributeCaptureEnabled?: boolean;
  alternativeFederationEngineEnabled: boolean;
  derivedPermissionsEnabled: boolean;
  notificationEnabled: boolean;
  refreshTokenEnabled: boolean;
  remoteAgentSettings?: IRemoteAgentSiteSettings;
  remoteQueryingEnabled: boolean;
  sheetImageEnabled: boolean;
  siteInviteEmailEnabled: boolean;
  generativeAiEnabled: boolean;
  extractEncryptionMode: string;
  cmekAvailable: boolean;
  isAdminDataSourceEnabled: boolean;
  siteStartPageUri: string;
  siteNamedSharingSettings: ISiteNamedSharingSettings;
  vizRecommendationsEnabled: boolean;
  vizRecUsernameVisibilityEnabled: boolean;
  sitePersonalSpaceSettings: ISitePersonalSpaceSettings;
  askDataMode: AskDataMode;
  explainDataEnabled: boolean;
  dataOrientationEnabled: boolean;
  dataStoryEnabled: boolean;
  workflowExtensionEnabled: boolean;
  userVisibility: UserVisibilityMode;
  requestAccessEnabled: boolean;
  siteCommentingSettings?: ISiteCommentingSettings;
  dataAlertsEnabled: boolean;
  metricsContentTypeEnabled: boolean;
  metricsContentTypeSnapshottingEnabled: boolean;
  metricsSnapshottingTimeZone: string;
  useDefaultMetricsSnapshottingTimeZone: boolean;
  webZoneContentEnabled: boolean;
  notifySiteAdminsOnThrottleEnabled: boolean;
  runNowEnabled: boolean;
  catalogObfuscationEnabled: boolean;
  timeZone: string;
  tagLimit: number;
  useDefaultTimeZone: boolean;
  publicCollectionsEnabled?: boolean;
  mobileBiometricsEnabled?: boolean;
  einsteinInFlowEnabled: boolean;
  canOwnerAccelerate?: boolean;
  linkedTaskSchedulingEnabled?: boolean;
  linkedTaskRunNowEnabled?: boolean;
  dqwSubscriptionsEnabled?: boolean;
  workbookAccelerationSettings?: IWorkbookAccelerationSettings;
  flowOutputSubscriptionSettings?: IFlowOutputSubscriptionSettings;
  flowParametersEnabled?: boolean;
  flowParametersAnyTypeEnabled?: boolean;
  flowParametersSystemTypeEnabled?: boolean;
  adminInsightsPublishFrequency: number;
  publishToSalesforceEnabled: boolean;
  collectionsEnabled: boolean;
  dataChangeRadarEnabled: boolean;
  subscriptionAndDDAEmailLinksEnabled: boolean;
  allowPatCreation?: boolean;
  allowPatGroup?: string;
  patAbsoluteExpiry?: number;
  autoFlowRunMonitoringEnabled: boolean;
  autoExtractRefreshMonitoringEnabled: boolean;
  emailCustomizationSettings: ISiteEmailCustomizationSettings;
}

// The getSiteSettingsForSiteAdmin returns the data as ISiteSettingsForSiteAdmin,
// but subscriptionSettings should really be inside siteAdminSettings.
// This is an adapter interface to make that happen until the API itself is updated.
export interface ISiteAdminSettingsWithSubscriptions extends ISiteAdminSettings {
  subscriptionSettings: ISiteSubscriptionSettings;
}

export interface IWorkbookAccelerationSettings {
  viewsLimitPerSiteForAccelerationEnabled: boolean;
  viewsLimitPerSiteForAcceleration: number;
  autoSuspendSettings: IAutoSuspendSettings;
}

export interface IAutoSuspendSettings {
  autoSuspendFailureLimitForAcceleration: number;
  autoSuspendFailureLimitForAccelerationEnabled: boolean;
  autoSuspendFailureLimitForAccelerationTimePeriod: string;
  autoSuspendAccelerationImprovementThresholdEnabled: boolean;
  autoSuspendAccelerationImprovementThreshold: number;
  autoSuspendViewsLimit: number;
  autoSuspendViewsLimitEnabled: boolean;
  autoSuspendViewsLimitTimePeriod: string;
}

export interface IFlowOutputSubscriptionSettings {
  flowOutputSubscriptionsEnabled?: boolean;
  flowOutputSubscriptionsDataInEmailBodyEnabled?: boolean;
  flowOutputSubscriptionsDataAsEmailAttachmentEnabled?: boolean;
}

export interface ISiteNamedSharingSettings {
  namedSharingEnabled: boolean;
}

export interface ISitePersonalSpaceSettings {
  personalSpaceEnabled: boolean;
  personalSpaceStorageQuotaSet: boolean;
  personalSpaceStorageQuota: number;
}

export interface ISiteEmailCustomizationSettings {
  /** Whether email customization for DDA and Subscription emails is on */
  isEmailCustomizationEnabled: boolean;
  /** The customer specified domain that is used in email hyperlinks and from address */
  emailCustomizationDomain: string;
  /** Port for SMTP server */
  emailCustomizationSMTPSPort: number;
  /** URL for SMTP server */
  emailCustomizationSMTPSUrl: string;
  /** Friendly name for sender from address */
  emailCustomizationSenderFriendlyName: string;
}

export interface ExtensionSafeListEntry {
  url: string;
  fullDataAllowed: boolean;
  promptNeeded: boolean;
}

export interface Option {
  name: string;
  value: boolean;
}

export interface ISiteDashExtSettingsForSiteAdmin {
  siteId: string;
  featureSettings: IDashExtSiteSettings;
}

export interface IDashExtSiteSettings {
  extensionsEnabled: boolean;
  allowSandboxed: boolean;
  safeList: ExtensionSafeListEntry[];
}

export interface IPrepExtensionsSiteSettings {
  einsteinEnabled: boolean;
}

export interface IPrepExtensionsServerSettings {
  einsteinEnabled: boolean;
}

export interface IAnalyticsExtSiteSettings {
  enabled: boolean;
}

export interface IAnalyticsExtConnectionId {
  connection_luid: string;
}

export enum AnalyticsExtConnectionType {
  TABPY ,
  RSERVE,
  EINSTEIN_DISCOVERY,
  GENERIC_API
}

export interface IAnalyticsExtConnectionSettings {
  host: string;
  port: number;
  is_auth_enabled: boolean;
  username: string;
  password: string;
  is_ssl_enabled: boolean;
  connection_brief: IAnalyticsConnectionBrief;
  connection_luid: string;
}

export interface IAnalyticsConnectionBrief {
  connection_name: string;
  connection_type: string;
}

export interface IAnalyticsConnectionMetadata {
  connection_luid: string;
  connection_brief: IAnalyticsConnectionBrief;
}

export interface IAnalyticsConnectionMapping {
  workbook_luid: string;
  connection_luid: string;
}

export interface IAnalyticsExtConnectionSettingsErrors {
  connection_luid: string;
  connection_type: string;
  connection_name: string;
  host: string;
  port: string;
  username: string;
  password: string;
}

export interface IAnalyticsExtConnectionId {
  connection_luid: string;
}

export interface IAnalyticsExtWorkbookId {
  workbook_luid: string;
}

export enum UserVisibilityMode {
  FULL = 'FULL',
  LIMITED = 'LIMITED',
}

export enum UserQuotaMode {
  SERVER_LIMIT = 'server-limit',
  SITE_USER_QUOTA = 'site-user-quota',
  SITE_TIERED_CAPACITY = 'site-tiered-capacity'
}

export interface ISiteSubscriptionSettings {
  subscriptionsEnabled: boolean;
  subscribeOthersEnabled: boolean;
  customScheduleSubscriptionForSiteEnabled: boolean;
  allowSubscriptionAttachments: boolean;
  serverAllowSubscriptionAttachments: boolean;
}
